package com.trevorwiebe.timesheet.loads.data

import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.data.Utils.errorWrapper
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.TSResult
import com.trevorwiebe.timesheet.loads.data.dto.LoadDto
import com.trevorwiebe.timesheet.loads.data.dto.toLoad
import com.trevorwiebe.timesheet.loads.data.dto.toLoadAttachmentDto
import com.trevorwiebe.timesheet.loads.data.dto.toLoadDto
import com.trevorwiebe.timesheet.loads.domain.LoadsRepository
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.domain.model.LoadAttachment
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.QuerySnapshot
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

class LoadsRepositoryImpl(
    private val firebaseDatabase: Firebase,
    private val coreRepository: CoreRepository
) : LoadsRepository {

    override suspend fun queryLoads(): Flow<TSResult> {
        val orgIdResult = coreRepository.getOrganizationId()
        if (orgIdResult.error != null) return kotlinx.coroutines.flow.flow { emit(orgIdResult) }
        val orgId = orgIdResult.data as String

        val collection = firebaseDatabase.firestore
            .collection("organizations")
            .document(orgId)
            .collection("loads")

        return loadsFlow(collection.snapshots)
    }

    override suspend fun queryDispatchedLoadsByDriverId(driverId: String): Flow<TSResult> {
        val orgIdResult = coreRepository.getOrganizationId()
        if (orgIdResult.error != null) return kotlinx.coroutines.flow.flow { emit(orgIdResult) }
        val orgId = orgIdResult.data as String

        val query = firebaseDatabase.firestore
            .collection("organizations")
            .document(orgId)
            .collection("loads")
            .where { "employeeId" equalTo driverId }
            .where { "dispatchConfirmed" equalTo true }

        return loadsFlow(query.snapshots)
    }

    private fun loadsFlow(snapshots: Flow<QuerySnapshot>): Flow<TSResult> {
        val flow = snapshots
            .map { snapshot ->
                snapshot.documents
                    .map { doc -> doc.data<LoadDto>().toLoad(id = doc.id) }
                    .sortedByDescending { it.originalDate }
            }
            .map { TSResult(data = it) }

        return FirestoreListenerRegistry.registerFlow(flow)
    }

    override suspend fun addLoad(load: Load): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            val loads = firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("loads")
            val ref = if (load.id.isEmpty()) loads.document else loads.document(load.id)
            ref.set(load.toLoadDto())
            TSResult(data = ref.id)
        }
    }

    override suspend fun updateLoad(load: Load): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("loads")
                .document(load.id)
                .set(load.toLoadDto())
            TSResult(data = load.id)
        }
    }

    override suspend fun updateAttachments(loadId: String, attachments: List<LoadAttachment>): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("loads")
                .document(loadId)
                .updateFields { "attachments" to attachments.map { it.toLoadAttachmentDto() } }
            TSResult(data = loadId)
        }
    }

    override suspend fun deleteLoad(loadId: String): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("loads")
                .document(loadId)
                .delete()
            TSResult(data = true)
        }
    }
}
